<script setup>
import StudentPage1 from '@/views/result/MLSRD-2026/student/Page1.vue';
import StudentPage2 from '@/views/result/MLSRD-2026/student/Page2.vue';
import StudentPage3 from '@/views/result/MLSRD-2026/student/Page3.vue';
import StudentPage4 from '@/views/result/MLSRD-2026/student/Page4.vue';
import StudentPage5 from '@/views/result/MLSRD-2026/student/Page5.vue';
import PageHeader from '@/components/result/MLSRD-2026/PageHeader.vue';
import PageFooter from '@/components/result/MLSRD-2026/PageFooter.vue';

import { ref, computed, onMounted, provide, inject } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/lib/api.js';

const route = useRoute();

// 학생 결과 데이터
const studentData = ref(null);
const loading = ref(true);

// 학교급 및 학년에 따른 점수 범위
const getScoreRange = (schulType, gradeCode) => {
  const key = `${schulType}${gradeCode}`;
  const ranges = {
    'E4': '1000~1700점',  // 초4
    'E6': '1050~1800점',  // 초6  
    'M2': '1100~1900점',  // 중2
    'H1': '1150~2000점'   // 고1
  };
  return ranges[key] || '';
};

// 학교급 및 학년 텍스트
const getGradeText = (schulType, gradeCode) => {
  const typeMap = { 'E': '초', 'M': '중', 'H': '고' };
  return `${typeMap[schulType] || ''}${gradeCode}`;
};

// 성별 변환
const getGenderText = (gender) => {
  return gender === 'M' ? '남' : gender === 'F' ? '여' : '';
};

// API 호출 함수
const fetchStudentResult = async () => {
  try {
    loading.value = true;
    const examId = route.query.exam_id;
    const userId = route.query.user_id;
    
    if (!examId) {
      throw new Error('exam_id가 없습니다.');
    }

    // API 요청 파라미터 구성
    const requestData = {
      exam_id: examId
    };
    
    // user_id가 있는 경우 추가
    if (userId) {
      requestData.user_id = userId;
    }

    console.log('학생 결과 API 요청:', requestData);

    const response = await api.post('/exam/stu/resultDetail', requestData);

    if (response.data.success) {
      studentData.value = response.data.result.result;
      console.log('학생 결과 데이터:', studentData.value);
      
      // 상위 컴포넌트에서 PDF 파일명 생성을 위해 전역에 저장
      window.currentStudentData = studentData.value;
    } else {
      throw new Error(response.data.message || '데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('학생 결과 데이터 조회 실패:', error);
  } finally {
    loading.value = false;
  }
};

const totalPages = 5;

// 서브헤더 텍스트 계산 - 기본 정보용
const baseSubHeaderText = computed(() => {
  if (!studentData.value) return '학생 결과 보고서';
  const studentName = studentData.value.user_nm || '학생';
  const schoolName = studentData.value.schul_nm || '학교';
  const gradeClass = studentData.value.school_cls ? `${studentData.value.grade_code}-${studentData.value.school_cls}` : '';
  return `${schoolName} ${gradeClass} ${studentName} 결과 보고서`;
});

// 페이지별 서브헤더 텍스트 생성
const getSubHeaderText = (pageIndex) => {
  if (!studentData.value) return '학생 결과';
  const studentName = studentData.value.user_nm || '학생';
  const schoolName = studentData.value.schul_nm || '학교';
  const gradeClass = studentData.value.school_cls ? `${studentData.value.grade_code}-${studentData.value.school_cls}` : '';
  
  switch (pageIndex) {
    case 0: // 1페이지
      return `진단검사 결과`;
    case 1: // 2페이지
    case 2: // 3페이지
      return `문해력 진단검사 결과`;
    case 3: // 4페이지
    case 4: // 5페이지
      return `수리력 진단검사 결과`;
    default:
      return `진단검사 결과`;
  }
};

// 하위 컴포넌트에서 사용할 수 있도록 provide
provide('studentData', studentData);
provide('loading', loading);

onMounted(() => {
  fetchStudentResult();
});
</script>

<template>
  <div class="result-container" style="background-color: white;">
    <!-- 학생 데이터를 PDF 생성기에서 접근할 수 있도록 숨겨진 요소에 저장 -->
    <div v-if="studentData" style="display: none;" data-student-data :data-student-data="JSON.stringify(studentData)"></div>
    
    <div class="page-wrapper">
      <PageHeader :page-index="0" report-type="student" :sub-header-text="getSubHeaderText(0)" />
      <StudentPage1 />
      <PageFooter :current-page="1" :total-pages="totalPages" />
    </div>
    <div class="page-wrapper">
      <PageHeader :page-index="1" report-type="student" :sub-header-text="getSubHeaderText(1)" />
      <StudentPage2 />
      <PageFooter :current-page="2" :total-pages="totalPages" />
    </div>
    <div class="page-wrapper">
      <PageHeader :page-index="2" report-type="student" :sub-header-text="getSubHeaderText(2)" />
      <StudentPage3 />
      <PageFooter :current-page="3" :total-pages="totalPages" />
    </div>
    <div class="page-wrapper">
      <PageHeader :page-index="3" report-type="student" :sub-header-text="getSubHeaderText(3)" />
      <StudentPage4 />
      <PageFooter :current-page="4" :total-pages="totalPages" />
    </div>
    <div class="page-wrapper">
      <PageHeader :page-index="4" report-type="student" :sub-header-text="getSubHeaderText(4)" />
      <StudentPage5 />
      <PageFooter :current-page="5" :total-pages="totalPages" />
    </div>
  </div>
</template>

<style>
.result-container {
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 20px 0;
}

.page-wrapper {
  width: 100%;
  min-height: 100vh;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  flex-direction: column;
}

.page-wrapper:last-child {
  border-bottom: none;
}

/* 페이지 콘텐츠 영역 */
.page-wrapper > :nth-child(2) {
  flex: 1;
  padding: 20px;
}

/* PDF 출력 시 */
@media print {
  .result-container {
    gap: 0;
    padding: 0;
  }
  
  .page-wrapper {
    min-height: auto;
    padding: 0;
    border-bottom: none;
    page-break-after: always;
    page-break-inside: avoid;
  }
  
  .page-wrapper:last-child {
    page-break-after: avoid;
  }
  
  .page-wrapper > :nth-child(2) {
    padding: 10px;
  }
}
</style>
